'use client'

import { CircleCheck, KeyRound, Mail, Phone, User as UserIcon, ImagePlus } from 'lucide-react'
import { Button } from '@/components/elements/Button'
import Input from '@/components/elements/Input'
import { Label } from '@/components/ui/label'
import Avatar from 'boring-avatars'
import { useCustomerSettings, useChangePassword } from '@/lib/commerce/hooks'
import { formatImageURL } from '@/utils/util'
import { PASSWORD_RULES } from '@/utils/auth'

export default function Settings({ customer, vendor }: { customer: any, vendor: any }) {
  const {
    firstName, setFirstName, lastName, setLastName, email, phone, setPhone,
    preview, loading, isImageUploaded, inputFile,
    handleFileChange, selectFile, handleSave,
  } = useCustomerSettings(customer)

  const {
    newPassword, setNewPassword, newPasswordConfirm, setNewPasswordConfirm,
    checks, isPasswordLoading, handlePasswordChange,
  } = useChangePassword()

  return (
    <div className="sm:p-3 space-y-5">
      <div>
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-[-0.7%]">Тохиргоо</h1>
        <p className="text-sm text-muted-foreground mt-1">Профайл, нэвтрэх мэдээллээ удирдана уу</p>
      </div>

      <SettingsSection title="Хувийн мэдээлэл" sub="Өөрийн мэдээллийг шинэчлэх">
        <div className="p-5 flex items-center gap-4">
          <div className="relative cursor-pointer group" onClick={selectFile}>
            {preview ? (
              <img src={preview} alt="profile" className="rounded-full w-16 h-16 object-cover ring-2 ring-white shadow" />
            ) : customer?.avatar ? (
              <img src={formatImageURL(customer.avatar)} alt="profile" className="rounded-full w-16 h-16 object-cover ring-2 ring-white shadow" />
            ) : (
              <div className="rounded-full overflow-hidden ring-2 ring-white shadow">
                <Avatar size={64} variant="marble" name={customer?.email || customer?.firstName || 'shop'} />
              </div>
            )}
            <div className="absolute inset-0 bg-black/40 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
              <ImagePlus className="w-5 h-5 text-white" />
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold truncate">{firstName} {lastName}</div>
            <div className="text-xs text-muted-foreground truncate">{email}</div>
          </div>
          <Button onClick={selectFile} variant="outline" size="sm" disabled={isImageUploaded}>
            {isImageUploaded ? 'Уншиж байна…' : 'Зураг солих'}
          </Button>
          <input type="file" accept="image/*" ref={inputFile} className="hidden" onChange={handleFileChange} />
        </div>

        <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-4 border-t border-border">
          <div>
            <Label className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground inline-flex items-center gap-1.5"><UserIcon className="w-3 h-3" />Нэр</Label>
            <div className="mt-1.5">
              <Input placeholder="Таны нэр" value={firstName} onChange={(e: any) => setFirstName(e.target.value)} color={vendor?.color?.primary} />
            </div>
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground inline-flex items-center gap-1.5"><UserIcon className="w-3 h-3" />Овог</Label>
            <div className="mt-1.5">
              <Input placeholder="Таны овог" value={lastName} onChange={(e: any) => setLastName(e.target.value)} color={vendor?.color?.primary} />
            </div>
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground inline-flex items-center gap-1.5"><Mail className="w-3 h-3" />И-мэйл</Label>
            <div className="mt-1.5">
              <Input placeholder="example@shop.mn" value={email} disabled color={vendor?.color?.primary} />
            </div>
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground inline-flex items-center gap-1.5"><Phone className="w-3 h-3" />Утасны дугаар</Label>
            <div className="mt-1.5">
              <Input placeholder="00000000" value={phone} onChange={(e: any) => setPhone(e.target.value)} color={vendor?.color?.primary} />
            </div>
          </div>
        </div>

        <div className="px-5 py-4 border-t border-border bg-zinc-50/40 flex justify-end">
          <Button onClick={handleSave} isLoading={loading} color={vendor?.color?.primary}>Хадгалах</Button>
        </div>
      </SettingsSection>

      <SettingsSection title="Нууц үг" sub="Аюулгүй байдлын тохиргоо">
        <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground inline-flex items-center gap-1.5"><KeyRound className="w-3 h-3" />Шинэ нууц үг</Label>
            <div className="mt-1.5">
              <Input type="password" placeholder="Шинэ нууц үг" value={newPassword} onChange={(e: any) => setNewPassword(e.target.value)} color={vendor?.color?.primary} />
            </div>
            <ul className="mt-3 space-y-1.5">
              {PASSWORD_RULES.map(rule => (
                <li key={rule.key} className="flex items-center gap-2 text-xs">
                  <CircleCheck className={`w-3.5 h-3.5 ${checks[rule.key] ? 'text-emerald-500' : 'text-zinc-300'}`} />
                  <span className={checks[rule.key] ? 'text-muted-foreground line-through' : 'text-muted-foreground'}>{rule.label}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground inline-flex items-center gap-1.5"><KeyRound className="w-3 h-3" />Дахин оруулна уу</Label>
            <div className="mt-1.5">
              <Input type="password" placeholder="Шинэ нууц үг давтах" value={newPasswordConfirm} onChange={(e: any) => setNewPasswordConfirm(e.target.value)} color={vendor?.color?.primary} />
            </div>
          </div>
        </div>
        <div className="px-5 py-4 border-t border-border bg-zinc-50/40 flex justify-end">
          <Button onClick={handlePasswordChange} isLoading={isPasswordLoading} color={vendor?.color?.primary}>Нууц үг шинэчлэх</Button>
        </div>
      </SettingsSection>
    </div>
  )
}

function SettingsSection({ title, sub, children }: { title: string, sub?: string, children: React.ReactNode }) {
  return (
    <section className="rounded-xl border border-border bg-card shadow-sm overflow-hidden">
      <div className="px-5 py-4 border-b border-border bg-zinc-50/30">
        <h3 className="font-semibold">{title}</h3>
        {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
      </div>
      <div>{children}</div>
    </section>
  )
}
